import {Desk, Employee, HibobResidence, INeighbourhood, KeySnapshot, Team, WorkSite} from "../api/Model";
import {EmployeeNeighbourhood} from "../api/EmployeeNeighbourhoodStore";
import {TeamTree} from "../api/TeamTreeStore";
import {WorkSites} from "../api/WorkSiteMapStore";
import {filterNotNull} from "./ArrayUtil";
import {EmployeeKeyHistory} from "../api/EmployeeKeyHistoryStore";
import {AssignedDesks} from "../api/AssignedDeskStore";

function addNode(
    root: HierarchyRoot,
    employeeMap: Map<String, HierarchyEmployee>,
    processedNodeIds: Set<string>,
    currentNode: HierarchyEmployee,
) {
    if (processedNodeIds.has(currentNode.hibobId)) return;
    processedNodeIds.add(currentNode.hibobId);

    const managerNode = currentNode.managerHibobId
        ? employeeMap.get(currentNode.managerHibobId)
        : undefined;

    if (managerNode) {
        addNode(root, employeeMap, processedNodeIds, managerNode);
        currentNode.parent = managerNode;
        managerNode.children.push(currentNode);
    } else {
        root.children.push(currentNode);
    }
}

export type HierarchyNode = HierarchyRoot | HierarchyEmployee;

export interface HierarchyRoot {
    type: 'root',
    fullName: string;
    neighbourhoods: INeighbourhood[];
    children: HierarchyEmployee[];
    match: boolean,
    length: number,
}

export interface HierarchyEmployee extends Employee {
    type: 'node';
    parent: HierarchyNode;
    neighbourhoods: INeighbourhood[];
    teamPath: Team[] | undefined,
    residence: HibobResidence & {workSite: WorkSite},
    keys: KeySnapshot[],
    assignedDesks: Desk[],
    children: HierarchyEmployee[];
    match: boolean,
    length: number,
}

export function emptyHierarchy(): HierarchyRoot {
    return {
        type: 'root',
        fullName: 'JetBrains',
        children: [],
        neighbourhoods: [],
        match: true,
        length: 1,
    };
}

export function buildHierarchy(
    employees: Employee[],
    employeeId2neighbourhood: EmployeeNeighbourhood,
    teamTree: TeamTree,
    workSites: WorkSites,
    employeeKeyHistory: EmployeeKeyHistory,
    assignedDesks: AssignedDesks,
): { root: HierarchyRoot, employees: HierarchyEmployee[] } {
    const employeeMap = new Map<string, HierarchyEmployee>();
    const root: HierarchyRoot = emptyHierarchy();
    employees.forEach(employee => {
        employeeMap.set(employee.hibobId, {
            ...employee,
            type: 'node',
            parent: root,
            neighbourhoods: employeeId2neighbourhood.get(employee),
            teamPath: teamTree.path(employee),
            residence: workSites.residenceByEmployee(employee),
            keys: employeeKeyHistory.active(employee),
            assignedDesks: assignedDesks.desksOf(employee),
            children: [],
            match: true,
            length: 1,
        });
    });


    const processedNodeIds = new Set<string>();
    employeeMap.forEach((node) => {
        addNode(root, employeeMap, processedNodeIds, node);
    });

    return {
        root,
        employees: filterNotNull(employees.map(employee => employeeMap.get(employee.hibobId))),
    };
}

export class SubtreeSize {
    rootSize: number = 1;
    nodeSizes: Map<number, number> = new Map();

    constructor(node: HierarchyNode) {
        this.countSizes(node);
    }

    getSize(node: HierarchyNode) {
        switch (node.type) {
            case "root":
                return this.rootSize;
            case "node":
                return this.nodeSizes.get(node.id) || 1;
        }
    }

    private setSize(node: HierarchyNode, size: number) {
        switch (node.type) {
            case "root":
                this.rootSize = size;
                break;
            case "node":
                this.nodeSizes.set(node.id, size);
                break;

        }
    }

    private countSizes(node: HierarchyNode): number {
        const size = node.children.reduce<number>(
            (acc, child) => acc + this.countSizes(child),
            1,
        );
        this.setSize(node, size);
        return size;
    }
}

export function filterHierarchy<N extends HierarchyNode>(
    srcNode: N,
    match: (node: HierarchyNode) => boolean
): N {
    const trgNode: N = {...srcNode, children: [], match: match(srcNode)};
    for (const child of srcNode.children) {
        const filteredChild = filterHierarchy(child, match);
        if (filteredChild.match || filteredChild.children.length > 0) {
            filteredChild.parent = trgNode;
            trgNode.children.push(filteredChild);
        }
    }
    return trgNode;
}

export function* dfsSequence(rootNode: HierarchyNode) {
    const stack = [{...rootNode, depth: 0}];

    do {
        const node = stack.pop();
        if (!node) break;

        yield node;
        for (let i = node.children.length - 1; i >= 0; i--) {
            stack.push({...node.children[i], depth: node.depth + 1});
        }
    } while (true);
}

export function* ancestorsSequence(node: HierarchyNode) {
    let currentNode = {...node, depth: 0};
    do {
        yield currentNode;
        if (currentNode.type === 'node') {
            currentNode = {...currentNode.parent, depth: currentNode.depth - 1};
        } else {
            break;
        }
    } while (true);
}

export function* relativesSequence(node: HierarchyNode) {
    const stack: ((HierarchyNode) & ({
        distance: number,
        kind: 'ancestor' | 'descendant' | 'self' | 'sibling',
        commonAncestor?: HierarchyEmployee & { distance: number },
    }))[] = [];

    const ancestorIds = new Set<number>();

    let distance = 0;
    let curNode = node;
    do {
        stack.unshift({
            ...curNode,
            distance,
            kind: distance === 0 ? 'self' : 'ancestor',
            commonAncestor: curNode.type === 'node' ? {...curNode, distance} : undefined,
        });
        distance++;
        if (curNode.type === 'node') {
            ancestorIds.add(curNode.id);
            curNode = curNode.parent
        } else {
            break;
        }
    } while (true);

    do {
        const curNode = stack.pop();
        if (!curNode) break;

        yield curNode;
        for (let i = curNode.children.length - 1; i >= 0; i--) {
            if (ancestorIds.has(curNode.children[i].id)) continue;

            let childKind: 'descendant' | 'sibling';
            switch (curNode.kind) {
                case 'ancestor':
                case 'sibling':
                    childKind = 'sibling';
                    break;
                case "self":
                case 'descendant':
                    childKind = 'descendant';
                    break;
            }
            stack.push({
                ...curNode.children[i],
                distance: curNode.distance + 1,
                kind: childKind,
                commonAncestor: curNode.commonAncestor,
            });
        }
    } while (true);

}
